import React, { useState, useEffect } from 'react';
import { exportPDF, getEarliestRecordDate } from '../utils/api';
import { toast } from 'sonner';
import { Download, Calendar, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import SoftBlurWrapper from '../components/SoftBlurWrapper';
import useUsageLimit from '../hooks/useUsageLimit';

const ExportPage = () => {
  const [loading, setLoading] = useState(false);
  const [loadingEarliest, setLoadingEarliest] = useState(false);
  const [hasData, setHasData] = useState(true);
  const [earliestDate, setEarliestDate] = useState(null);
  
  // Usage limit for soft blur gating
  const { isFreeLimitReached, loading: usageLoading } = useUsageLimit();
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Fetch earliest record date on mount for "All" button
  useEffect(() => {
    const fetchEarliestDate = async () => {
      try {
        const result = await getEarliestRecordDate();
        setHasData(result.has_data);
        setEarliestDate(result.earliest_date);
      } catch (error) {
        console.error('Failed to fetch earliest date:', error);
        setHasData(false);
      }
    };
    fetchEarliestDate();
  }, []);

  const handleExport = async () => {
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date must be before end date');
      return;
    }

    setLoading(true);
    try {
      const blob = await exportPDF(startDate, endDate);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `health-summary-${startDate}-to-${endDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="export-page" className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-2">Export Report</h1>
        <p className="text-lg leading-relaxed text-slate-700">Generate a PDF summary for your healthcare provider</p>
      </div>

      {/* Export section - Wrapped with Soft Blur for Free Plan Gating */}
      <SoftBlurWrapper isBlurred={isFreeLimitReached && !usageLoading}>
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Export Form */}
        <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
          <h2 className="text-2xl font-semibold text-slate-800 mb-6">Select Date Range</h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Start Date</label>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type="date"
                  data-testid="export-start-date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full h-12 pl-12 pr-4 rounded-xl border border-slate-200 bg-white text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">End Date</label>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type="date"
                  data-testid="export-end-date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full h-12 pl-12 pr-4 rounded-xl border border-slate-200 bg-white text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
              </div>
            </div>

            <Button
              onClick={handleExport}
              disabled={loading}
              data-testid="export-pdf-button"
              className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Generating PDF...
                </>
              ) : (
                <>
                  <Download className="h-5 w-5" />
                  Generate PDF Report
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Information Card */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-primary/10 rounded-xl">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">What's Included</h3>
                <p className="text-slate-600 leading-relaxed">
                  Your PDF report will contain a comprehensive summary of your health data for the selected period
                </p>
              </div>
            </div>
            
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="inline-block w-2 h-2 bg-primary rounded-full mt-2 mr-3"></span>
                <span className="text-slate-700">All blood test results with values and dates</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-2 h-2 bg-primary rounded-full mt-2 mr-3"></span>
                <span className="text-slate-700">Current medications and supplements</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-2 h-2 bg-primary rounded-full mt-2 mr-3"></span>
                <span className="text-slate-700">Reported symptoms with dates and notes</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-2 h-2 bg-primary rounded-full mt-2 mr-3"></span>
                <span className="text-slate-700">Medical disclaimer for healthcare use</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
            <h3 className="text-base font-semibold text-blue-800 mb-2">Preparing for Your Appointment</h3>
            <p className="text-sm text-blue-700 leading-relaxed">
              Bring this PDF to your doctor appointments to provide a clear overview of your health tracking. 
              It can help facilitate more productive discussions about your treatment and progress.
            </p>
          </div>
        </div>
      </div>
      </SoftBlurWrapper>

      {/* Quick Date Range Buttons */}
      <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Select</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => {
              const end = new Date();
              const start = new Date();
              start.setMonth(start.getMonth() - 6);
              setStartDate(start.toISOString().split('T')[0]);
              setEndDate(end.toISOString().split('T')[0]);
            }}
            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-full hover:bg-slate-100 transition-colors font-medium"
          >
            Last 6 Months
          </button>
          <button
            onClick={() => {
              const end = new Date();
              const start = new Date();
              start.setMonth(start.getMonth() - 12);
              setStartDate(start.toISOString().split('T')[0]);
              setEndDate(end.toISOString().split('T')[0]);
            }}
            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-full hover:bg-slate-100 transition-colors font-medium"
          >
            Last 12 Months
          </button>
          <button
            onClick={() => {
              const end = new Date();
              const start = new Date();
              start.setMonth(start.getMonth() - 24);
              setStartDate(start.toISOString().split('T')[0]);
              setEndDate(end.toISOString().split('T')[0]);
            }}
            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-full hover:bg-slate-100 transition-colors font-medium"
          >
            Last 24 Months
          </button>
          <button
            onClick={() => {
              const end = new Date();
              const start = new Date();
              start.setMonth(start.getMonth() - 36);
              setStartDate(start.toISOString().split('T')[0]);
              setEndDate(end.toISOString().split('T')[0]);
            }}
            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-full hover:bg-slate-100 transition-colors font-medium"
          >
            Last 36 Months
          </button>
          <button
            onClick={async () => {
              if (!hasData) {
                toast.error('No data available yet');
                return;
              }
              
              if (earliestDate) {
                setStartDate(earliestDate);
                setEndDate(new Date().toISOString().split('T')[0]);
              } else {
                // Fallback: re-fetch if not loaded
                setLoadingEarliest(true);
                try {
                  const result = await getEarliestRecordDate();
                  if (result.has_data && result.earliest_date) {
                    setEarliestDate(result.earliest_date);
                    setStartDate(result.earliest_date);
                    setEndDate(new Date().toISOString().split('T')[0]);
                    setHasData(true);
                  } else {
                    toast.error('No data available yet');
                    setHasData(false);
                  }
                } catch (error) {
                  toast.error('Failed to load earliest date');
                } finally {
                  setLoadingEarliest(false);
                }
              }
            }}
            disabled={!hasData || loadingEarliest}
            className={`px-4 py-2 border rounded-full font-medium transition-colors ${
              !hasData
                ? 'bg-slate-100 border-slate-300 text-slate-400 cursor-not-allowed'
                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
            }`}
            title={!hasData ? 'No data available yet' : 'Export all historical data'}
          >
            {loadingEarliest ? 'Loading...' : 'All'}
          </button>
        </div>
        {!hasData && (
          <p className="text-sm text-slate-500 mt-3">
            Add some health records to enable the "All" export option
          </p>
        )}
      </div>
    </div>
  );
};

export default ExportPage;
